// src/components/Dashboard/NotesTab/hooks/useTaskInfiniteScroll.js

import { useCallback, useRef, useEffect } from 'react';

export const useTaskInfiniteScroll = ({
  taskId,
  jobId,
  hasMore,
  loading,
  loadMore,
  enabled = true
}) => {
  const observerRef = useRef(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(hasMore); // Track hasMore state

  // Update ref when hasMore changes
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const lastNoteRef = useCallback((node) => {
    // Don't set up observer if not enabled or no more notes to load
    if (!enabled || loading || !hasMoreRef.current) {
      return;
    }
    
    if (loadingRef.current) {
      return;
    }
    
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver((entries) => {
      // Only trigger if we're intersecting AND we have more notes AND not loading
      if (entries[0].isIntersecting && hasMoreRef.current && !loading && !loadingRef.current) {
        loadingRef.current = true;
        loadMore(taskId, jobId).finally(() => {
          loadingRef.current = false;
        });
      }
    }, {
      root: null,
      rootMargin: '200px',
      threshold: 0.1
    });

    if (node) {
      observerRef.current.observe(node);
    }
  }, [enabled, loading, loadMore, taskId, jobId]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { lastNoteRef };
};